import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { CalendarIcon, Filter } from 'lucide-react';
import { getAdblockLegacyDomains } from '../../../services/domains';
import { Domain } from '../../../types';
import { cn } from '../../../utils/utils';

interface Props {
  networkId: number;
  onChange: (filters: LegacyFilters) => void;
}

export interface LegacyFilters {
  startDate: string;
  endDate: string;
  domains: number[]; // domain IDs
}

export const AdblockLegacyFilters: React.FC<Props> = ({ networkId, onChange }) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('last7days');
  const [filters, setFilters] = useState<LegacyFilters>({
    startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    domains: []
  });
  const [allDomains, setAllDomains] = useState<Domain[]>([]);
  const [loadingDomains, setLoadingDomains] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load domains
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingDomains(true);
        const domains = await getAdblockLegacyDomains(networkId);
        setAllDomains(domains);
      } catch (e:any) {
        console.error(e);
        setError('Erreur chargement domaines');
      } finally {
        setLoadingDomains(false);
      }
    };
    load();
  }, [networkId]);

  // Emit changes
  useEffect(() => {
    onChange(filters);
  }, [filters, onChange]);

  const handlePresetChange = (preset: string) => {
    const now = dayjs();
    let startDate: string;
    let endDate = now.format('YYYY-MM-DD');

    switch (preset) {
      case 'last7days':
        startDate = now.subtract(7, 'day').format('YYYY-MM-DD');
        break;
      case 'last14days':
        startDate = now.subtract(14, 'day').format('YYYY-MM-DD');
        break;
      case 'last30days':
        startDate = now.subtract(30, 'day').format('YYYY-MM-DD');
        break;
      case 'lastMonth':
        const lastMonth = now.subtract(1, 'month');
        startDate = lastMonth.startOf('month').format('YYYY-MM-DD');
        endDate = lastMonth.endOf('month').format('YYYY-MM-DD');
        break;
      default:
        return;
    }

    setSelectedPreset(preset);
    setFilters(prev => ({ ...prev, startDate, endDate }));
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) return;
    setSelectedPreset('');
    setFilters(prev => ({ ...prev, startDate: dayjs(date).format('YYYY-MM-DD') }));
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (!date) return;
    setSelectedPreset('');
    setFilters(prev => ({ ...prev, endDate: dayjs(date).format('YYYY-MM-DD') }));
  };

  const toggleDomain = (id: number) => {
    setFilters(prev => ({
      ...prev,
      domains: prev.domains.includes(id) ? prev.domains.filter(d => d !== id) : [...prev.domains, id]
    }));
  };

  const resetFilters = () => {
    setSelectedPreset('last7days');
    setFilters({
      startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      domains: []
    });
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Filtres
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-6 flex-col lg:flex-row">
          {/* Presets */}
            <div className="space-y-2 w-full lg:w-auto">
              <Label>Période</Label>
              <Tabs value={selectedPreset} onValueChange={handlePresetChange} className="w-full">
                <TabsList className="grid w-full grid-cols-4 h-auto">
                  <TabsTrigger value="last7days" className="py-2 px-3 w-auto">7 jours</TabsTrigger>
                  <TabsTrigger value="last14days" className="py-2 px-3 w-auto">14 jours</TabsTrigger>
                  <TabsTrigger value="last30days" className="py-2 px-3 w-auto">30 jours</TabsTrigger>
                  <TabsTrigger value="lastMonth" className="py-2 px-3 w-auto">Mois dernier</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          {/* Right side filters */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
            {/* Start Date */}
            <div className="space-y-1">
              <Label className="text-sm">Date début</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full h-9 justify-start text-left font-normal", !filters.startDate && 'text-muted-foreground')}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dayjs(filters.startDate).format('DD/MM/YYYY')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dayjs(filters.startDate).toDate()}
                    onSelect={handleStartDateChange}
                    disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            {/* End Date */}
            <div className="space-y-1">
              <Label className="text-sm">Date fin</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("w-full h-9 justify-start text-left font-normal", !filters.endDate && 'text-muted-foreground')}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dayjs(filters.endDate).format('DD/MM/YYYY')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dayjs(filters.endDate).toDate()}
                    onSelect={handleEndDateChange}
                    disabled={(date) => date > new Date() || date < new Date('1900-01-01') || date < dayjs(filters.startDate).toDate()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            {/* Domains Multi-select (simple custom list for now) */}
            <div className="space-y-1 md:col-span-2">
              <Label className="text-sm">Domaines (Legacy)</Label>
              <div className="border rounded p-2 h-40 overflow-auto text-sm space-y-1">
                {loadingDomains && <div className="text-muted-foreground">Chargement...</div>}
                {error && <div className="text-destructive">{error}</div>}
                {!loadingDomains && !error && allDomains.length === 0 && (
                  <div className="text-muted-foreground">Aucun domaine legacy actif</div>
                )}
                {allDomains.map(domain => {
                  const active = filters.domains.includes(domain.id);
                  return (
                    <button
                      key={domain.id}
                      type="button"
                      onClick={() => toggleDomain(domain.id)}
                      className={cn(
                        'w-full flex justify-between items-center px-2 py-1 rounded border text-left',
                        active ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'
                      )}
                    >
                      <span>{domain.name}</span>
                      {active && <span className="text-xs font-medium">✓</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Reset */}
            <div className="space-y-1">
              <Label className="text-sm">&nbsp;</Label>
              <Button variant="outline" className="w-full h-9" onClick={resetFilters}>Réinitialiser</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdblockLegacyFilters;